/**
 * The /budget reads and its one write (docs/superpowers/specs/2026-10-28-native-budget-mmm-design.md), on the browser
 * client (the caller's RLS). Every read THROWS on an error: a failed read is its box's error, never an empty answer.
 * - The config: mmm_configs, which members may SELECT (plan R5). null = nothing confirmed yet (R3).
 * - The active version: get_mmm_active_version, SECURITY DEFINER with a membership check, because only the master may
 *   read mmm_model_versions. The stored bundle is parsed again on every read.
 * - The readiness: get_mmm_readiness (migration 131). null = a workspace the caller cannot see.
 * - The ad accounts: the five granted columns of media_platform_accounts, by name (`*` is a 403), analytics left out,
 *   paged by id.
 * - The write: set_mmm_config (SECURITY DEFINER, admin or master), because the table grants no INSERT or UPDATE.
 *
 * @module services/budget/budgetReaders
 */
import { getSupabaseClient } from '@/config';
import { errorMessageOf, readAllRows } from '@/services/supabasePaging';
import type { Database, Json } from '@/types/database.types';
import { OUTCOME_METRICS, type OutcomeMetric } from './budgetContract';
import { parseBundle, type Bundle } from './bundleSchema';
import { readinessOf, type ReadinessRow } from './readinessRules';
import type { AdAccount } from './scopeRows';

type AdAccountDb = Pick<Database['public']['Tables']['media_platform_accounts']['Row'],
  'id' | 'account_name' | 'external_account_id' | 'platform' | 'currency'>;

/** The confirmed MMM config (migration 130). */
export interface MmmConfig {
  workspaceId: string;
  /** set_mmm_config stores it as a sorted set. */
  scopeAccountIds: readonly string[];
  outcomeMetric: OutcomeMetric;
  confirmedBy: string;
  confirmedAt: string;
}

/** get_mmm_active_version's row, camelCased, with its bundle parsed. It is also readinessRules' ActiveSummary. */
export interface ActiveVersion {
  id: string;
  version: number;
  datasetId: string;
  /** The dataset's metric and scope, which check 6 compares with the config's. */
  outcomeMetric: OutcomeMetric;
  scopeAccountIds: readonly string[];
  /** Week labels (Mondays, plan R1): the data's last day is windowEnd + 6. */
  windowStart: string;
  windowEnd: string;
  trainerVersion: string;
  engineVersion: string;
  /** As stored and unchecked. The upload stores the bundle's own diagnostics here, and bundle.diagnostics is the parsed copy. */
  diagnostics: Json;
  bundle: Bundle;
  uploadedAt: string;
}

/** mmm_configs' CHECK and the dataset route hold the metric to the four; any other value fails the read. */
const metricOf = (v: string): OutcomeMetric => {
  const metric = OUTCOME_METRICS.find((m) => m === v);
  if (!metric) throw new Error(`budget: outcome metric "${v}" is not one of ${OUTCOME_METRICS.join(', ')}`);
  return metric;
};

export const readConfig = async (workspaceId: string): Promise<MmmConfig | null> => {
  const { data, error } = await getSupabaseClient()
    .from('mmm_configs')
    .select('workspace_id, scope_account_ids, outcome_metric, confirmed_by, confirmed_at')
    .eq('workspace_id', workspaceId)
    .maybeSingle();
  if (error) throw new Error(`budget config read failed: ${errorMessageOf(error)}`);
  if (!data) return null;
  return {
    workspaceId: data.workspace_id, scopeAccountIds: data.scope_account_ids, outcomeMetric: metricOf(data.outcome_metric),
    confirmedBy: data.confirmed_by, confirmedAt: data.confirmed_at,
  };
};

export const readActiveVersion = async (workspaceId: string): Promise<ActiveVersion | null> => {
  const { data, error } = await getSupabaseClient().rpc('get_mmm_active_version', { p_workspace_id: workspaceId }).maybeSingle();
  if (error) throw new Error(`budget active version read failed: ${errorMessageOf(error)}`);
  if (!data) return null;
  // Review Focus 5: a stored bundle the schema refuses is the box's error, never a partial page.
  const parsed = parseBundle(data.bundle);
  if (!parsed.ok) throw new Error(`stored bundle invalid: ${parsed.issues[0]}`);
  return {
    id: data.id, version: data.version, datasetId: data.dataset_id, outcomeMetric: metricOf(data.outcome_metric),
    scopeAccountIds: data.scope_account_ids, windowStart: data.window_start, windowEnd: data.window_end,
    trainerVersion: data.trainer_version, engineVersion: data.engine_version, diagnostics: data.diagnostics,
    bundle: parsed.bundle, uploadedAt: data.uploaded_at,
  };
};

export const readReadiness = async (workspaceId: string): Promise<ReadinessRow | null> => {
  const { data, error } = await getSupabaseClient().rpc('get_mmm_readiness', { p_workspace_id: workspaceId }).maybeSingle();
  if (error) throw new Error(`budget readiness read failed: ${errorMessageOf(error)}`);
  return data ? readinessOf(data) : null;
};

export const readAdAccounts = async (workspaceId: string): Promise<AdAccount[]> => {
  const { rows, error } = await readAllRows<AdAccountDb>(
    (from, to) => getSupabaseClient()
      .from('media_platform_accounts')
      .select('id, account_name, external_account_id, platform, currency')
      .eq('workspace_id', workspaceId)
      .neq('platform', 'analytics')
      .order('id', { ascending: true })
      .range(from, to),
    'budget ad accounts',
  );
  if (error) throw new Error(`budget ad accounts read failed: ${errorMessageOf(error)}`);
  // The currency as stored: set_mmm_config and the readiness test `currency IS NOT NULL`, and a trim here would disagree.
  return rows.map((r) => ({
    id: r.id, accountName: r.account_name, externalAccountId: r.external_account_id, platform: r.platform, currency: r.currency,
  }));
};

/** set_mmm_config checks every id and stores the scope as a sorted set. */
export const writeConfig = async (workspaceId: string, scopeIds: readonly string[], metric: OutcomeMetric): Promise<void> => {
  const { error } = await getSupabaseClient()
    .rpc('set_mmm_config', { p_workspace_id: workspaceId, p_scope_account_ids: [...scopeIds], p_outcome_metric: metric });
  if (error) throw new Error(`budget config write failed: ${errorMessageOf(error)}`);
};
